/**
 * AP Invoices Demo — oRPC procedure resolver.
 *
 * THROWAWAY DEMO CODE. Maps oRPC procedure paths to fixtures from the AP demo
 * store. Returns `undefined` for anything it doesn't own so non-AP procedures
 * (auth, nav, org config, …) fall through to the real backend. Read procedures
 * return fixtures; mutations the demo UI can trigger actually mutate the
 * in-memory store and return the updated entity, so edits, acceptances,
 * payments and (un)matching all stick — the mutation hooks invalidate the
 * relevant queries, which re-read the store through this resolver.
 */

import {
  SystemEntity,
  EntityAssociationType,
  APInvoiceStatus,
  PaymentStatus,
  MatchConfidenceStatus,
} from '@erp/shared-types';
import type {
  APInvoice,
  EntityAssociation,
  EntityAssociationItem,
} from '@erp/rpc';
import type { DemoProcedureResolver } from '@/demo/core/demo-client';
import type { ApDemoFixtureState } from './fixture-store.types';
import { getApDemoState, updateApDemoState } from './fixture-store';
import { AP_DEMO_IDS, BRAND } from './fixture-seed';

function asRecord(input: unknown): Record<string, unknown> {
  return (input ?? {}) as Record<string, unknown>;
}

const MATCH_LINE_ENTITY_TYPES: SystemEntity[] = [
  SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
  SystemEntity.AR_SALES_ORDER_LINE_ITEM,
  SystemEntity.CHARGE,
];

/** Both copies of an invoice (list row + detail) live in the store; patch both. */
function eachInvoiceCopy(
  state: ApDemoFixtureState,
  invoiceId: string,
  fn: (inv: APInvoice) => void,
): void {
  const detailInvoice = state.invoiceDetails[invoiceId]?.invoice;
  if (detailInvoice) fn(detailInvoice);
  const listInv = state.invoices.find((i) => i.id === invoiceId);
  if (listInv) fn(listInv);
}

/** Re-derive an invoice's line-item match summary from its associations. */
function recomputeMatchSummary(
  state: ApDemoFixtureState,
  invoiceId: string,
): void {
  const lineItems = state.invoiceDetails[invoiceId]?.lineItems ?? [];
  let confirmed = 0;
  let tentative = 0;
  let matched = 0;
  for (const li of lineItems) {
    const assocs = state.associations[li.id] ?? [];
    const lineMatch = assocs.find((a) =>
      MATCH_LINE_ENTITY_TYPES.includes(a.entity.entityType),
    );
    if (!lineMatch) continue;
    matched += 1;
    if (lineMatch.metadata?.matchStatus === MatchConfidenceStatus.TENTATIVE) {
      tentative += 1;
    } else {
      confirmed += 1;
    }
  }
  const total = lineItems.length;
  eachInvoiceCopy(state, invoiceId, (inv) => {
    inv.lineItemMatchSummary = {
      matched,
      confirmed,
      tentative,
      unmatched: Math.max(0, total - matched),
      total,
    };
  });
}

/** Apply a metadata edit from `invoices.update` to both invoice copies. */
function applyInvoiceUpdate(
  state: ApDemoFixtureState,
  inp: Record<string, unknown>,
): void {
  const id = String(inp.id);
  eachInvoiceCopy(state, id, (inv) => {
    if ('supplierId' in inp) {
      const supplierId =
        typeof inp.supplierId === 'string' ? inp.supplierId : null;
      inv.supplierId = supplierId;
      const supplier = supplierId ? state.suppliers[supplierId] : undefined;
      inv.supplier = supplier
        ? {
            id: supplier.id,
            name: supplier.name,
            contactName: supplier.contactName,
            email: supplier.email,
            phone: supplier.phone,
            paymentTerms: supplier.paymentTerms,
            status: supplier.status,
            notes: null,
            defaultPaymentMethod: 'ach',
            glAccounts: null,
          }
        : null;
    }
    if ('referenceIds' in inp) {
      inv.referenceIds = Array.isArray(inp.referenceIds)
        ? inp.referenceIds.filter((v): v is string => typeof v === 'string')
        : null;
    }
    // Non-nullable string fields — only overwrite when a string is provided.
    for (const key of ['invoiceDate', 'dueDate'] as const) {
      const val = inp[key];
      if (typeof val === 'string') {
        inv[key] = val;
      }
    }
    // Nullable string fields — `null` clears them.
    for (const key of [
      'preferredPayDate',
      'scheduledPaymentDate',
      'scheduledPaymentExplanation',
      'notes',
      'internalNotes',
    ] as const) {
      if (key in inp) {
        const val = inp[key];
        inv[key] = typeof val === 'string' ? val : null;
      }
    }
    if ('paymentHold' in inp && typeof inp.paymentHold === 'boolean') {
      inv.paymentHold = inp.paymentHold;
    }
  });
}

function findAssociationItem(
  assocs: EntityAssociationItem[] | undefined,
  targetEntityId: string,
): EntityAssociationItem | undefined {
  return assocs?.find((a) => a.entity.entityId === targetEntityId);
}

export const apDemoResolver: DemoProcedureResolver = (path, input) => {
  const key = path.join('.');
  const state = getApDemoState();
  const inp = asRecord(input);

  switch (key) {
    // --- reads ---
    case 'accountsPayable.invoices.list':
      return {
        invoices: state.invoices,
        pagination: {
          total: state.invoices.length,
          limit: 25,
          offset: 0,
        },
      };
    case 'accountsPayable.invoices.get':
      return state.invoiceDetails[String(inp.id)];
    case 'accountsPayable.invoices.listChangeLog':
      return { entries: state.changeLogs[String(inp.apInvoiceId)] ?? [] };
    case 'accountsPayable.invoices.listSupportingDocuments':
      return { documents: state.supportingDocs[String(inp.apInvoiceId)] ?? [] };
    case 'communications.emailThreads.getThread':
      return state.emailThreads[String(inp.conversationId)];
    case 'accountsPayable.invoices.getExtractionResult':
      return { extractionResult: null };
    case 'accountsPayable.suppliers.get':
      return state.suppliers[String(inp.id)];
    case 'accountsPayable.purchaseOrders.get':
      return state.purchaseOrders[String(inp.id)];
    case 'accountsPayable.purchaseOrders.getLineItem':
      return state.poLineItems[String(inp.id)];
    case 'core.entityAssociations.list':
      return { associations: state.associations[String(inp.entityId)] ?? [] };
    case 'core.documents.getPreview':
      return {
        signedUrl:
          String(inp.documentId) === AP_DEMO_IDS.podDocument
            ? BRAND.podImageUrl
            : BRAND.invoiceImageUrl,
      };

    // --- payments page (finance domain) ---
    case 'finance.payments.list': {
      const totalAmount = state.payments
        .reduce((sum, p) => sum + Number(p.amount), 0)
        .toFixed(2);
      return {
        payments: state.payments,
        total: state.payments.length,
        totalAmount,
      };
    }
    case 'finance.payments.get':
      return (
        state.payments.find((p) => p.id === String(inp.id)) ?? state.payments[0]
      );
    case 'finance.transactions.list':
      return {
        transactions: state.transactions,
        total: state.transactions.length,
        lastSyncTime: null,
      };

    // --- shipments + charges (freight invoice ↔ shipment ↔ PO) ---
    case 'supplyChain.shipments.get':
      return state.shipments[String(inp.id)];
    case 'finance.charges.getById':
      return { charge: state.charges[String(inp.id)] ?? null };
    case 'finance.charges.getForEntity': {
      const entity = asRecord(inp.entity);
      const entityId =
        typeof entity.entityId === 'string' ? entity.entityId : '';
      return {
        charges: Object.values(state.charges).filter(
          (c) => c.docId === entityId,
        ),
      };
    }

    // --- mutations (mutate the in-memory store, then return the entity) ---
    case 'accountsPayable.invoices.update': {
      updateApDemoState((s) => {
        applyInvoiceUpdate(s, inp);
      });
      return (
        getApDemoState().invoiceDetails[String(inp.id)]?.invoice ??
        getApDemoState().invoices[0]
      );
    }
    case 'accountsPayable.invoices.acceptMatches': {
      const id = String(inp.id);
      updateApDemoState((s) => {
        eachInvoiceCopy(s, id, (inv) => {
          inv.status = APInvoiceStatus.ACCEPTED;
          inv.approvedAt = new Date().toISOString();
        });
      });
      return getApDemoState().invoiceDetails[id]?.invoice;
    }
    case 'accountsPayable.invoices.unacceptMatches': {
      const id = String(inp.id);
      updateApDemoState((s) => {
        const summary = s.invoiceDetails[id]?.invoice?.lineItemMatchSummary;
        const revertTo =
          summary && summary.total > 0 && summary.unmatched === 0
            ? APInvoiceStatus.MATCHED
            : APInvoiceStatus.DRAFT;
        eachInvoiceCopy(s, id, (inv) => {
          inv.status = revertTo;
          inv.approvedAt = null;
          inv.approvedByUserId = null;
        });
      });
      return getApDemoState().invoiceDetails[id]?.invoice;
    }
    case 'accountsPayable.invoices.createPayment': {
      const id = String(inp.id);
      updateApDemoState((s) => {
        eachInvoiceCopy(s, id, (inv) => {
          inv.status = APInvoiceStatus.FINALIZED;
          inv.paymentStatus = PaymentStatus.PAID;
        });
      });
      return getApDemoState().invoiceDetails[id]?.invoice;
    }
    case 'accountsPayable.invoices.updateLineItem': {
      const invoiceId = String(inp.apInvoiceId);
      const lineId = String(inp.id);
      updateApDemoState((s) => {
        const line = s.invoiceDetails[invoiceId]?.lineItems.find(
          (li) => li.id === lineId,
        );
        if (!line) return;
        if ('quantity' in inp) line.quantity = Number(inp.quantity);
        if ('unitPrice' in inp && typeof inp.unitPrice === 'string') {
          line.unitPrice = inp.unitPrice;
        }
        const qty = Number(line.quantity) || 0;
        const unit = Number(line.unitPrice) || 0;
        line.totalPrice = (qty * unit).toFixed(2);
      });
      const detail = getApDemoState().invoiceDetails[invoiceId];
      return (
        detail?.lineItems.find((li) => li.id === lineId) ?? detail?.lineItems[0]
      );
    }
    case 'accountsPayable.invoices.delete': {
      const id = String(inp.id);
      updateApDemoState((s) => {
        s.invoices = s.invoices.filter((i) => i.id !== id);
        delete s.invoiceDetails[id];
      });
      return { success: true };
    }

    // --- entity association mutations (line ↔ PO / document matching) ---
    case 'core.entityAssociations.confirmMatch': {
      const sourceId = String(inp.sourceEntityId);
      const targetId = String(inp.targetEntityId);
      updateApDemoState((s) => {
        const assoc = findAssociationItem(s.associations[sourceId], targetId);
        if (assoc) {
          assoc.metadata = {
            ...(assoc.metadata ?? {}),
            matchStatus: MatchConfidenceStatus.CONFIRMED,
          };
        }
        for (const inv of s.invoices) recomputeMatchSummary(s, inv.id);
      });
      return { success: true };
    }
    case 'core.entityAssociations.delete': {
      const sourceId = String(inp.sourceEntityId);
      const targetId = String(inp.targetEntityId);
      updateApDemoState((s) => {
        const existing = s.associations[sourceId];
        if (existing) {
          s.associations[sourceId] = existing.filter(
            (a) => a.entity.entityId !== targetId,
          );
        }
        for (const inv of s.invoices) recomputeMatchSummary(s, inv.id);
      });
      return { success: true };
    }
    case 'core.entityAssociations.create': {
      const sourceId = String(inp.sourceEntityId);
      const targetId = String(inp.targetEntityId);
      const targetType =
        typeof inp.targetEntityType === 'string'
          ? (inp.targetEntityType as SystemEntity)
          : SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM;
      const associationType =
        typeof inp.associationType === 'string'
          ? (inp.associationType as EntityAssociationType)
          : EntityAssociationType.AP_INVOICE_MATCHING;
      updateApDemoState((s) => {
        const item: EntityAssociationItem = {
          entity: { entityId: targetId, entityType: targetType },
          associationType,
          metadata: null, // manual match → treated as CONFIRMED
        };
        const existing = s.associations[sourceId] ?? [];
        s.associations[sourceId] = [
          ...existing.filter((a) => a.entity.entityType !== targetType),
          item,
        ];
        for (const inv of s.invoices) recomputeMatchSummary(s, inv.id);
      });
      const now = new Date();
      const association: EntityAssociation = {
        id: '4a000000-0000-4000-8000-000000000001',
        organizationId: AP_DEMO_IDS.org,
        entityIdSource: sourceId,
        entityTypeSource:
          typeof inp.sourceEntityType === 'string'
            ? (inp.sourceEntityType as SystemEntity)
            : SystemEntity.AP_INVOICE_LINE_ITEM,
        entityIdTarget: targetId,
        entityTypeTarget: targetType,
        associationType,
        createdBy: null,
        createdAt: now,
        updatedAt: now,
      };
      return association;
    }

    // --- memory bank notes (demo) ---
    // Notes are non-persistent in the demo: `listUsages` always returns the
    // empty baseline and create/update/delete are no-ops. So adding a note
    // shows its success toast and then resets — the list refetch that follows
    // finds nothing new, leaving the panel back at its baseline.
    case 'memoryBank.listUsages':
      return { usages: [] };
    case 'memoryBank.create':
    case 'memoryBank.update':
      return {
        id: 'b1000000-0000-4000-8000-000000000001',
        note: typeof inp.note === 'string' ? inp.note : '',
        metadata: null,
      };
    case 'memoryBank.delete':
      return { success: true };

    default:
      return undefined;
  }
};
